import { useState, type FormEvent } from 'react';
import { ApiError, type ApiErrorCode } from '../api/client';
import type { LoginInput } from '../api/auth';
import { readLastTenant, saveLastTenant } from '../lib/lastTenant';
import { useT, type TFn } from '../i18n';

interface Props {
  onSubmit: (input: LoginInput) => Promise<void>;
}

function errorMessage(e: unknown, t: TFn): string {
  if (e instanceof ApiError) {
    const map: Partial<Record<ApiErrorCode, string>> = {
      auth_failed: t('auth.error.invalidCredentials'),
      tenant_suspended: t('auth.error.tenantSuspended'),
      forbidden: t('auth.error.forbidden'),
      rate_limited: t('auth.error.rateLimited'),
      network: t('auth.error.network'),
      invalid_payload: t('auth.error.invalidPayload'),
    };
    return map[e.code] ?? `${t('common.errorPrefix')}: ${e.message}`;
  }
  return t('common.unknownError');
}

export function LoginForm({ onSubmit }: Props) {
  const t = useT();
  // ANX-117 — если попали сюда из-за истёкшей сессии (redirectToLogin) —
  // объясняем, почему пришлось войти заново, а не молчим.
  const [sessionExpired] = useState(
    () => new URLSearchParams(window.location.search).get('expired') === '1',
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  // ANX-102 — Chrome не автозаполняет наш нестандартный третий логин-поле,
  // поэтому префиллим последний слаг сами.
  const [tenantSlug, setTenantSlug] = useState(readLastTenant);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit =
    !pending && email.trim() !== '' && password !== '' && tenantSlug.trim() !== '';

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setPending(true);
    const slug = tenantSlug.trim().toLowerCase();
    saveLastTenant(slug);
    try {
      await onSubmit({
        email: email.trim().toLowerCase(),
        password,
        tenantSlug: slug,
      });
    } catch (err) {
      setError(errorMessage(err, t));
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-3 w-full max-w-sm">
      {sessionExpired && error === null && (
        <p role="status" className="text-sm text-amber-700">
          {t('auth.sessionExpired')}
        </p>
      )}
      <label className="flex flex-col gap-1">
        <span className="text-sm text-slate-700">{t('auth.email')}</span>
        <input
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="border border-slate-300 rounded px-3 py-2"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-sm text-slate-700">{t('auth.password')}</span>
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="border border-slate-300 rounded px-3 py-2"
        />
      </label>
      <label className="flex flex-col gap-1">
        <span className="text-sm text-slate-700">{t('auth.tenant')}</span>
        <input
          type="text"
          autoComplete="organization"
          placeholder={t('auth.tenantHint')}
          value={tenantSlug}
          onChange={(e) => setTenantSlug(e.target.value)}
          className="border border-slate-300 rounded px-3 py-2"
        />
      </label>
      {error !== null && (
        <p role="alert" className="text-sm text-red-600">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={!canSubmit}
        className="bg-slate-900 text-white rounded px-3 py-2 disabled:opacity-50"
      >
        {pending ? t('auth.submitting') : t('auth.submit')}
      </button>
      <a
        href="/forgot-password"
        className="text-sm text-center text-accent-600 hover:text-accent-700"
      >
        {t('auth.forgotPassword')}
      </a>
    </form>
  );
}
